import React, { useEffect, useState } from 'react'
import { Users, UserCheck, TrendingUp, Activity, Calendar, BarChart3, PieChart, LineChart, RefreshCw } from 'lucide-react'
import StatCard from '../components/dashboard/StatCard.jsx'
import {
  Getstats,
  GetSubscribedUsersStats,
  GetRevenueAnalytics,
  GetUserEngagementAnalytics,
  GetTrainerPerformanceAnalytics,
  GetContentAnalytics,
  GetSystemHealthMetrics,
  AdminGetPendingTestimonialCount
} from '../components/Api.jsx'
import { Chart } from 'primereact/chart'
import { Card } from 'primereact/card'
import { ProgressBar } from 'primereact/progressbar'
import { Badge } from 'primereact/badge'
import { Divider } from 'primereact/divider'

export default function Dashboard() {
  const [Stats, setStats] = useState({})
  const [subscribedUsersStats, setSubscribedUsersStats] = useState({})
  const [revenueAnalytics, setRevenueAnalytics] = useState({})
  const [userEngagementAnalytics, setUserEngagementAnalytics] = useState({})
  const [trainerPerformanceAnalytics, setTrainerPerformanceAnalytics] = useState({})
  const [contentAnalytics, setContentAnalytics] = useState({})
  const [systemHealthMetrics, setSystemHealthMetrics] = useState({})
  const [isClient, setIsClient] = useState(false)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [pendingTestimonialCount, setPendingTestimonialCount] = useState(0)

  useEffect(() => {
    setIsClient(true)
  }, [])

  const fetchAllDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }

      // Fetch all dashboard data in parallel
      const [
        statsResult,
        subscribedUsersResult,
        revenueResult,
        userEngagementResult,
        trainerPerformanceResult,
        contentResult,
        systemHealthResult,
        testimonialCountResult
      ] = await Promise.allSettled([
        Getstats(),
        GetSubscribedUsersStats(),
        GetRevenueAnalytics(),
        GetUserEngagementAnalytics(),
        GetTrainerPerformanceAnalytics(),
        GetContentAnalytics(),
        GetSystemHealthMetrics(),
        AdminGetPendingTestimonialCount()
      ])

      if (statsResult.status === 'fulfilled') {
        setStats(statsResult.value.data || {})
      }
      if (subscribedUsersResult.status === 'fulfilled') {
        setSubscribedUsersStats(subscribedUsersResult.value.data || {})
      }
      if (revenueResult.status === 'fulfilled') {
        setRevenueAnalytics(revenueResult.value.data || {})
      }
      if (userEngagementResult.status === 'fulfilled') {
        setUserEngagementAnalytics(userEngagementResult.value.data || {})
      }
      if (trainerPerformanceResult.status === 'fulfilled') {
        setTrainerPerformanceAnalytics(trainerPerformanceResult.value.data || {})
      }
      if (contentResult.status === 'fulfilled') {
        setContentAnalytics(contentResult.value.data || {})
      }
      if (systemHealthResult.status === 'fulfilled') {
        setSystemHealthMetrics(systemHealthResult.value.data || {})
      }
      if (testimonialCountResult.status === 'fulfilled') {
        setPendingTestimonialCount(testimonialCountResult.value.data?.pendingCount || 0)
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error)
    } finally {
      if (isRefresh) {
        setRefreshing(false)
      } else {
        setLoading(false)
      }
    }
  }

  useEffect(() => {
    if (isClient) {
      fetchAllDashboardData()
    }
  }, [isClient])

  const handleRefresh = () => {
    fetchAllDashboardData(true)
  }

  if (!isClient || loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  // Chart configurations
  const revenueChartData = {
    labels: revenueAnalytics.monthlyRevenueData?.map(item => item.month) || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Revenue (INR)',
        data: revenueAnalytics.monthlyRevenueData?.map(item => item.amount) || [10000, 12000, 15000, 18000, 20000, 22000],
        borderColor: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4
      }
    ]
  }

  const userEngagementChartData = {
    labels: ['Active Users', 'Inactive Users', 'New Users'],
    datasets: [
      {
        data: [
          userEngagementAnalytics.activeUsers || 0,
          userEngagementAnalytics.inactiveUsers || 0,
          userEngagementAnalytics.newUsers || 0
        ],
        backgroundColor: ['#10B981', '#F59E0B', '#3B82F6'],
        borderWidth: 0
      }
    ]
  }

  // Process subscription data from API - show only top 3 plans
  const processSubscriptionData = () => {
    if (!subscribedUsersStats.subscriptionsByPlan || !Array.isArray(subscribedUsersStats.subscriptionsByPlan)) {
      return {
        labels: ['No Data'],
        data: [0],
        totalSubscriptions: 0
      };
    }

    const planCounts = {};
    let totalSubscriptions = 0;

    subscribedUsersStats.subscriptionsByPlan.forEach(plan => {
      const planName = plan.planName || 'Unknown Plan';
      const userCount = plan.users ? plan.users.length : 0;
      planCounts[planName] = userCount;
      totalSubscriptions += userCount;
    });

    // Sort plans by user count (descending) and take top 3
    const sortedPlans = Object.entries(planCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    const top3PlanCounts = {};
    sortedPlans.forEach(([planName, count]) => {
      top3PlanCounts[planName] = count;
    });

    return {
      labels: Object.keys(top3PlanCounts),
      data: Object.values(top3PlanCounts),
      totalSubscriptions,
      planCounts: top3PlanCounts
    };
  };

  const subscriptionData = processSubscriptionData();

  const subscriptionDistributionData = {
    labels: subscriptionData.labels,
    datasets: [
      {
        data: subscriptionData.data,
        backgroundColor: ['#8B5CF6', '#06B6D4', '#10B981'],
        borderWidth: 0
      }
    ]
  }

  const trainerPerformanceData = {
    labels: trainerPerformanceAnalytics.trainerStatsBySpecialty ? Object.keys(trainerPerformanceAnalytics.trainerStatsBySpecialty) : ['Fitness', 'Nutrition', 'Yoga'],
    datasets: [
      {
        label: 'Trainers by Specialty',
        data: trainerPerformanceAnalytics.trainerStatsBySpecialty ? Object.values(trainerPerformanceAnalytics.trainerStatsBySpecialty) : [15, 12, 8],
        backgroundColor: '#3B82F6',
        borderColor: '#1D4ED8',
        borderWidth: 1
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#000'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e5e7eb'
        },
        ticks: {
          color: '#000'
        }
      },
      x: {
        grid: {
          color: '#e5e7eb'
        },
        ticks: {
          color: '#000'
        }
      }
    }
  }

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#000'
        }
      }
    }
  }

  const stats = [
    {
      title: 'Total Users',
      value: Stats.totalUsers || 0,
      icon: Users,
      color: 'text-blue-500',
      change: userEngagementAnalytics.userGrowthRate ? `+${userEngagementAnalytics.userGrowthRate}%` : '+12.5%'
    },
    {
      title: 'Active Subscriptions',
      value: Stats.activeSubscriptions || 0,
      icon: UserCheck,
      color: 'text-green-500',
      change: subscribedUsersStats.subscriptionGrowthRate ? `+${subscribedUsersStats.subscriptionGrowthRate}%` : '+5.2%'
    },
    {
      title: 'Total Revenue',
      value: Stats.totalRevenue ? `₹${Stats.totalRevenue.toLocaleString()}` : '₹0',
      icon: TrendingUp,
      color: 'text-purple-500',
      change: revenueAnalytics.revenueGrowthRate ? `+${revenueAnalytics.revenueGrowthRate}%` : '+8.3%'
    },
    {
      title: 'Active Trainers',
      value: trainerPerformanceAnalytics.totalTrainers || 0,
      icon: Activity,
      color: 'text-orange-500',
      change: trainerPerformanceAnalytics.trainerUtilizationRate ? `${trainerPerformanceAnalytics.trainerUtilizationRate}%` : '72%'
    },
    {
      title: 'Available Trainers',
      value: trainerPerformanceAnalytics.availableTrainers || 0,
      icon: Calendar,
      color: 'text-indigo-500',
      change: trainerPerformanceAnalytics.meetingUtilizationRate ? `${trainerPerformanceAnalytics.meetingUtilizationRate}%` : '65%'
    }
    // ,
    // {
    //   title: 'System Health',
    //   value: systemHealthMetrics.systemStatus || 'HEALTHY',
    //   icon: BarChart3,
    //   color: systemHealthMetrics.systemStatus === 'HEALTHY' ? 'text-green-500' : 'text-red-500',
    //   change: systemHealthMetrics.uptime ? `${systemHealthMetrics.uptime}%` : '99.8%'
    // }
  ]



  return (
    <div className="min-h-screen p-4">
      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    Welcome to the BYB Dashboard where you can seamlessly track, monitor, and analyze user activity.
                    Analyze your Revenue, study your top performing plans, content and trainers all on one, easy to access screen.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh dashboard data"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Refresh Indicator */}
      {refreshing && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-800">Updating dashboard data</p>
              <p className="text-sm text-blue-600">Please wait while we fetch the latest information...</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {stats.map((stat, index) => (
          <StatCard key={stat.title} stat={stat} index={index} />
        ))}
      </div>

      {/* Charts Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Revenue Analytics */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-black flex items-center">
              <div className="p-1.5 bg-green-100 rounded-lg mr-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              Revenue Analytics
            </h3>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-xs text-gray-500">Live</span>
            </div>
          </div>

          <div className="h-48 mb-4 relative">
            <Chart type="line" data={revenueChartData} options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                legend: {
                  display: false
                }
              },
              elements: {
                line: {
                  tension: 0.4,
                  borderWidth: 2,
                  borderColor: '#10B981'
                },
                point: {
                  radius: 4,
                  hoverRadius: 6,
                  backgroundColor: '#10B981',
                  borderColor: '#ffffff',
                  borderWidth: 1
                }
              },
              scales: {
                ...chartOptions.scales,
                y: {
                  ...chartOptions.scales.y,
                  grid: {
                    color: '#e5e7eb'
                  },
                  ticks: {
                    ...chartOptions.scales.y.ticks,
                    color: '#000',
                    callback: function (value) {
                      return '₹' + value.toLocaleString();
                    }
                  }
                },
                x: {
                  ...chartOptions.scales.x,
                  grid: {
                    display: false
                  },
                  ticks: {
                    color: '#000'
                  }
                }
              }
            }} />

            <div className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded p-2 border border-gray-200">
              <p className="text-xs text-gray-500">Current Month</p>
              <p className="text-xs font-semibold text-black">₹{revenueAnalytics.monthlyRevenue || 0}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-green-50 border border-green-100 rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500">Monthly Revenue</p>
                <div className="p-1 bg-green-100 rounded">
                  <TrendingUp className="w-3 h-3 text-green-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-green-700">₹{revenueAnalytics.monthlyRevenue || 0}</p>
              <div className="flex items-center mt-1">
                <TrendingUp className="w-3 h-3 text-green-600 mr-1" />
                <span className="text-xs text-green-600">+{revenueAnalytics.revenueGrowthRate || 0}% this month</span>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-gray-500">Total Revenue</p>
                <div className="p-1 bg-blue-100 rounded">
                  <BarChart3 className="w-3 h-3 text-blue-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-blue-700">₹{Stats.totalRevenue || 0}</p>
              <div className="flex items-center mt-1">
                <span className="text-xs text-blue-600">All time revenue</span>
              </div>
            </div>
          </div>
        </div>

        {/* User Engagement */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-black flex items-center">
              <div className="p-1.5 bg-blue-100 rounded-lg mr-2">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              User Engagement
            </h3>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              <span className="text-xs text-gray-500">Real-time</span>
            </div>
          </div>

          <div className="h-48 mb-4 relative flex items-center justify-center">
            <Chart type="doughnut" data={userEngagementChartData} options={{
              ...pieChartOptions,
              plugins: {
                ...pieChartOptions.plugins,
                legend: {
                  display: false
                }
              },
              elements: {
                arc: {
                  borderWidth: 0
                }
              },
              cutout: '60%',
              radius: '80%'
            }} />

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-bold text-black">{userEngagementAnalytics.activeUsers || 0}</p>
                <p className="text-xs text-gray-500">Active Users</p>
              </div>
            </div>
          </div>

          <div className="space-y-2 mt-8">
            <div className="flex items-center justify-between p-2 bg-green-50 border border-green-100 rounded">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-xs text-gray-700">Active Users</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-green-700">{userEngagementAnalytics.activeUsers || 0}</p>
                <p className="text-xs text-gray-500">Currently online</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-yellow-50 border border-yellow-100 rounded">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-xs text-gray-700">Inactive Users</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-yellow-700">{userEngagementAnalytics.inactiveUsers || 0}</p>
                <p className="text-xs text-gray-500">Offline users</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-2 bg-blue-50 border border-blue-100 rounded">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-gray-700">New Users</span>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-blue-700">{userEngagementAnalytics.newUsers || 0}</p>
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
          </div>
        </div>

        {/* Top 3 Subscription Plans */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-black flex items-center">
              <div className="p-1.5 bg-purple-100 rounded-lg mr-2">
                <PieChart className="w-4 h-4 text-purple-600" />
              </div>
              Top 3 Subscription Plans
            </h3>
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
              <span className="text-xs text-gray-500">Updated</span>
            </div>
          </div>

          <div className="h-48 mb-4 relative flex items-center justify-center">
            <Chart type="pie" data={subscriptionDistributionData} options={{
              ...pieChartOptions,
              plugins: {
                ...pieChartOptions.plugins,
                legend: {
                  display: false
                }
              },
              elements: {
                arc: {
                  borderWidth: 0
                }
              },
              radius: '75%'
            }} />

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-lg font-bold text-black">
                  {subscriptionData.totalSubscriptions}
                </p>
                <p className="text-xs text-black">Total Subscriptions</p>
              </div>
            </div>
          </div>

          <div className="space-y-2 mt-6">
            {subscriptionData.labels.map((planName, index) => {
              const colors = [
                { bg: 'bg-purple-50', border: 'border-purple-100', dot: 'bg-purple-500', text: 'text-purple-700' },
                { bg: 'bg-cyan-50', border: 'border-cyan-100', dot: 'bg-cyan-500', text: 'text-cyan-700' },
                { bg: 'bg-green-50', border: 'border-green-100', dot: 'bg-green-500', text: 'text-green-700' }
              ];
              const color = colors[index];
              const value = subscriptionData.data[index];
              const percentage = subscriptionData.totalSubscriptions > 0 ? Math.round((value / subscriptionData.totalSubscriptions) * 100) : 0;

              return (
                <div key={planName} className={`flex items-center justify-between p-2 ${color.bg} border ${color.border} rounded`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 ${color.dot} rounded-full`}></div>
                    <span className="text-xs text-gray-700">{planName}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-black">{value}</p>
                    <p className="text-xs text-gray-500">{percentage}% of total</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Trainer Performance */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold text-black mb-3 flex items-center">
            <BarChart3 className="w-4 h-4 mr-2 text-orange-600" />
            Trainer Performance Analytics
          </h3>
          <div className="h-48">
            <Chart type="bar" data={trainerPerformanceData} options={{
              ...chartOptions,
              plugins: {
                ...chartOptions.plugins,
                legend: {
                  display: false
                }
              },
              scales: {
                ...chartOptions.scales,
                y: {
                  ...chartOptions.scales.y,
                  grid: {
                    color: '#e5e7eb'
                  },
                  ticks: {
                    color: '#000'
                  }
                },
                x: {
                  ...chartOptions.scales.x,
                  grid: {
                    display: false
                  },
                  ticks: {
                    color: '#000'
                  }
                }
              }
            }} />
          </div>
          <div className="space-y-1.5 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Total Trainers</span>
              <span className="text-xs font-semibold text-black">{trainerPerformanceAnalytics.totalTrainers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Available Trainers</span>
              <span className="text-xs font-semibold text-black">{trainerPerformanceAnalytics.availableTrainers || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-700">Avg Rating</span>
              <span className="text-xs font-semibold text-black">{trainerPerformanceAnalytics.averageTrainerRating || 0}/5</span>
            </div>
          </div>
        </div>

        {/* System Health Metrics */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold text-black mb-3 flex items-center">
            <Activity className="w-4 h-4 mr-2 text-green-600" />
            System Health Metrics
          </h3>
          <div className="space-y-4">


            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-700">Trainer Availability</span>
                <span className="text-xs font-semibold text-blue-600">{systemHealthMetrics.trainerAvailabilityRate || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${systemHealthMetrics.trainerAvailabilityRate || 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-700">Meeting Slot Utilization</span>
                <span className="text-xs font-semibold text-purple-600">{systemHealthMetrics.meetingSlotUtilization || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${systemHealthMetrics.meetingSlotUtilization || 0}%` }}
                ></div>
              </div>
            </div>

            {/* <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-700">System Status</span>
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${systemHealthMetrics.systemStatus === 'HEALTHY'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                  }`}>
                  {systemHealthMetrics.systemStatus || 'HEALTHY'}
                </span>
              </div>
            </div> */}

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
              <div className="text-center p-2 bg-blue-50 rounded border border-blue-100">
                <p className="text-lg font-bold text-blue-700">{systemHealthMetrics.totalUsers || 0}</p>
                <p className="text-xs text-black">Total Users</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded border border-green-100">
                <p className="text-lg font-bold text-green-700">{systemHealthMetrics.availableTrainers || 0}</p>
                <p className="text-xs text-black">Available Trainers</p>
              </div>
            </div>
          </div>
        </div>

        {/* Addon Plans Analytics */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <h3 className="font-semibold text-black mb-3 flex items-center">
            <BarChart3 className="w-4 h-4 mr-2 text-indigo-600" />
            Addon Plans Analytics
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 bg-blue-50 rounded border border-blue-100">
                <BarChart3 className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-blue-700">{contentAnalytics.totalAddonPlans || 0}</p>
                <p className="text-xs text-gray-500">Total Addon Plans</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded border border-green-100">
                <BarChart3 className="w-6 h-6 text-green-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-green-700">₹{contentAnalytics.averageAddonPrice || 0}</p>
                <p className="text-xs text-gray-500">Avg Addon Price</p>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-700">Price Range</span>
                <span className="text-xs font-semibold text-indigo-600">₹{contentAnalytics.minAddonPrice || 0} - ₹{contentAnalytics.maxAddonPrice || 0}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-500 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(100, ((contentAnalytics.averageAddonPrice || 0) / (contentAnalytics.maxAddonPrice || 1)) * 100)}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-medium text-gray-700">Average Duration</span>
                <span className="text-xs font-semibold text-cyan-600">{contentAnalytics.averageAddonDuration || 0} days</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-cyan-500 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${Math.min(100, ((contentAnalytics.averageAddonDuration || 0) / 90) * 100)}%` }}
                ></div>
              </div>
            </div>

            <div className="text-center pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Categories Distribution</p>
              <div className="flex flex-wrap justify-center gap-1">
                {contentAnalytics.addonStatsByCategory && Object.entries(contentAnalytics.addonStatsByCategory).map(([category, count]) => (
                  <span key={category} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                    {category}: {count}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="text-center">
          <div className="flex flex-col items-center justify-center gap-3">
            {/* <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">BYB Admin Dashboard</p>
                  <p className="text-xs text-gray-500">Advanced Analytics Platform</p>
                </div>
              </div> */}
            <div className="text-center">
              <p className="text-sm text-gray-600">Powered by TCY Consulting</p>
              <p className="text-xs text-gray-500 mt-1">
                Last updated: {new Date().toLocaleString()}
                {refreshing && <span className="ml-1 text-blue-500">• Refreshing...</span>}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
